// Jul-2026 — Returnable Tracking Report
// Which returnable items/assets came back and which are still out —
// driven by the returnable_register (created on IV approval, closed by RVs).
// Endpoints: POST /api/reports/returnables/summary | /export.xlsx

import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import { FileSpreadsheet, Filter, RotateCw, Undo2 } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

const EMPTY = { from_date: "", to_date: "", branch_id: "", project_id: "", line_type: "", status: "", condition: "", search: "" };
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const lblCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1";

const STATUS_BADGE = {
  "Returned": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Partially Returned": "bg-amber-100 text-amber-800 border-amber-200",
  "Pending": "bg-rose-100 text-rose-800 border-rose-200",
};

const CONDITION_BADGE = {
  "Good": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Damaged": "bg-amber-100 text-amber-800 border-amber-200",
  "Needs Repair": "bg-orange-100 text-orange-800 border-orange-200",
  "Lost": "bg-red-100 text-red-800 border-red-200",
};

export default function ReturnablesReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [b, p] = await Promise.all([api.get("/branches"), api.get("/projects-picker")]);
        setBranches(Array.isArray(b.data) ? b.data : b.data?.items || []);
        setProjects(Array.isArray(p.data) ? p.data : p.data?.items || []);
      } catch { /* optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/returnables/summary", f);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the report");
    } finally { setLoading(false); }
  }

  async function doExport() {
    setExporting(true);
    try {
      const res = await api.post("/reports/returnables/export.xlsx", filters, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "returnable_tracking_report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    } finally { setExporting(false); }
  }

  const rows = data?.rows || [];
  const pg = useClientPagination(rows, { initialPageSize: 50 });
  const t = data?.totals || {};
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const cards = [
    { key: "lines", label: "Returnable Lines", tone: "border-slate-300 bg-slate-100 text-slate-900" },
    { key: "pending", label: "Pending", tone: "border-rose-200 bg-rose-50 text-rose-900" },
    { key: "partial", label: "Partially Returned", tone: "border-amber-200 bg-amber-50 text-amber-900" },
    { key: "returned", label: "Returned", tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
    { key: "overdue", label: "Overdue", tone: "border-red-300 bg-red-50 text-red-900" },
    { key: "qty_pending", label: "Qty Still Out", tone: "border-orange-200 bg-orange-50 text-orange-900" },
    { key: "damaged", label: "Damaged / Repair", tone: "border-amber-300 bg-amber-100 text-amber-900" },
    { key: "lost", label: "Lost", tone: "border-red-300 bg-red-100 text-red-900" },
    { key: "written_off_value", label: "Written-Off Value", tone: "border-slate-400 bg-slate-200 text-slate-900", money: true },
  ];

  return (
    <div data-testid="returnables-page">
      <PageHeader
        eyebrow="Documents & Reports"
        title="Returnable Tracking Report"
        subtitle="Every returnable item/asset issued on Issue Vouchers — what has come back through Return Vouchers, what is still out, and what is overdue."
        actions={
          <button data-testid="rt-export-xlsx" disabled={exporting || loading} onClick={doExport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Excel"}
          </button>
        }
      />

      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2"><Filter size={12} /> Filters</h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div><div className={lblCls}>Issued From</div>
            <input data-testid="rt-from" type="date" value={filters.from_date} onChange={(e) => set("from_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>Issued To</div>
            <input data-testid="rt-to" type="date" value={filters.to_date} onChange={(e) => set("to_date", e.target.value)} className={selCls} /></div>
          <div><div className={lblCls}>Branch</div>
            <select data-testid="rt-branch" value={filters.branch_id} onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select></div>
          <div><div className={lblCls}>Project</div>
            <select data-testid="rt-project" value={filters.project_id} onChange={(e) => set("project_id", e.target.value)} className={selCls}>
              <option value="">All Projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.project_name}</option>)}
            </select></div>
          <div><div className={lblCls}>Status</div>
            <select data-testid="rt-status" value={filters.status} onChange={(e) => set("status", e.target.value)} className={selCls}>
              <option value="">All Statuses</option>
              <option value="pending">Pending (nothing back)</option>
              <option value="partial">Partially Returned</option>
              <option value="returned">Fully Returned</option>
              <option value="overdue">Overdue Only</option>
            </select></div>
          <div><div className={lblCls}>Line Type</div>
            <select data-testid="rt-line-type" value={filters.line_type} onChange={(e) => set("line_type", e.target.value)} className={selCls}>
              <option value="">Material + Asset</option>
              <option value="Material">Material</option>
              <option value="Asset">Asset</option>
            </select></div>
          <div><div className={lblCls}>Condition of Return</div>
            <select data-testid="rt-condition" value={filters.condition} onChange={(e) => set("condition", e.target.value)} className={selCls}>
              <option value="">All Conditions</option>
              <option value="Good">Good</option>
              <option value="Damaged">Damaged</option>
              <option value="Needs Repair">Needs Repair</option>
              <option value="Lost">Lost</option>
            </select></div>
          <div className="lg:col-span-2"><div className={lblCls}>Search</div>
            <input data-testid="rt-search" value={filters.search} onChange={(e) => set("search", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="Voucher no · party · item · code · batch · project…" className={selCls} /></div>
          <div className="flex gap-2 lg:col-span-2">
            <button data-testid="rt-apply" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button data-testid="rt-reset" onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Reset</button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`rt-card-${c.key}`} className={`border px-4 py-3 ${c.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{c.label}</div>
            <div className="text-xl font-bold mt-0.5">{c.money ? formatINR(t[c.key] ?? 0) : (t[c.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      {t.truncated && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Showing the first 5,000 rows — narrow the filters to see everything.
        </div>
      )}

      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Undo2 size={14} className="text-orange-600" />
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Returnable Register ({rows.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="returnables-table" className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                {["Issue Voucher", "Issued", "Expected Return", "Branch", "Project", "Party", "Item / Asset", "Code"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-bold">{h}</th>
                ))}
                <th className="px-3 py-2.5 font-bold text-right">Issued</th>
                <th className="px-3 py-2.5 font-bold text-right">Returned</th>
                <th className="px-3 py-2.5 font-bold text-right">Pending</th>
                <th className="px-3 py-2.5 font-bold text-right">Value</th>
                <th className="px-3 py-2.5 font-bold">Condition</th>
                <th className="px-3 py-2.5 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={14} className="px-4 py-12 text-center text-slate-400">No returnable lines match these filters.</td></tr>
              ) : pg.paged.map((r, i) => (
                <tr key={i} data-testid={`rt-row-${i}`}
                  className={`border-t border-slate-100 hover:bg-orange-50/40 ${r.overdue ? "bg-red-50/60" : ""}`}>
                  <td className="px-3 py-2 whitespace-nowrap font-bold text-slate-900">{r.voucher_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.issued_date}</td>
                  <td className={`px-3 py-2 whitespace-nowrap ${r.overdue ? "text-red-700 font-bold" : ""}`}>
                    {r.expected_return_date || "—"}{r.overdue && <span className="ml-1 text-[9px] uppercase font-bold bg-red-600 text-white px-1 py-0.5">Overdue</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.branch_name}</td>
                  <td className="px-3 py-2">{r.project_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">{r.party_type}</span>{r.party_name}
                    {r.party_code && <span className="ml-1 font-mono text-[10px] text-slate-500">({r.party_code})</span>}
                  </td>
                  <td className="px-3 py-2">{r.item_name}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{r.code}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.qty_issued}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.qty_returned}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.qty_pending > 0 ? "text-rose-700" : "text-slate-400"}`}>{r.qty_pending}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.value)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.condition ? (
                      <div>
                        <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CONDITION_BADGE[r.condition] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{r.condition}</span>
                        {r.outcome && <div className="text-[10px] text-slate-500 mt-0.5 max-w-[200px] whitespace-normal">{r.outcome}</div>}
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE[r.status] || ""}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-slate-100">
          <PaginationControls page={pg.page} pageSize={pg.pageSize} total={pg.total}
            onPage={pg.setPage} onPageSize={pg.setPageSize} pageSizeOptions={[25, 50, 100, 200]} />
        </div>
      </Card>
    </div>
  );
}
